import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, map, Observable, of, take, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { QueryParams } from 'src/app/core/models/query-params.model';
import { Arquivo, ContextoLeituraArquivo } from 'src/app/core/models/arquivo.model';
import { AppLocalStorageService } from './app-local-storage.service';
import { AuthService } from 'src/app/shared/services/auth.service';
import { PopupService } from './popup.service';
import { asSubmitError, SubmitError } from 'src/app/core/utils/solicitacao-form.util';

import { SgrResponse } from 'src/app/core/models/response.model';

@Injectable({
  providedIn: 'root',
})
export class FileService {
  private url: string = environment.API;
  private urlIntranet: string = environment.API_INTRANET;
  private openTabsFile: { key: string; value: { [key: string]: any } };
  private readonly openBlobTabs = new Map<string, OpenBlobTab>();
  private readonly maxOpenBlobTabKeyLength = 128;
  constructor(
    private httpClient: HttpClient,
    private appLocalStorageService: AppLocalStorageService,
    private authService: AuthService,
    private popupService: PopupService
  ) {
    this.openTabsFile = this.getOpenTabsFile();
    // Verifica se há abas abertas com arquivos e as fecha. (Esse código somente é executado com o recarregamento da página)
    if (this.openTabsFile.value) {
      Object.keys(this.openTabsFile.value).forEach(tabKey => {
        const tabData = this.openTabsFile.value[tabKey];
        // Obtém a referência da aba aberta
        const existingWindow = window.open('', tabData.tabId);
        if (existingWindow && !existingWindow.closed) {
          existingWindow.close(); // Fecha a aba, pois a URL do arquivo foi revogada
        }
        delete this.openTabsFile.value[tabKey];
      });
      localStorage.setItem(this.openTabsFile.key, JSON.stringify(this.openTabsFile.value));
    }
  }

  //! ========================= MÉTODOS LOCAIS =========================

  // Converte arquivos para a base 64
  convertFileToBase64(file: File): Observable<string> {
    return new Observable<string>(observer => {
      const reader = new FileReader();

      reader.readAsDataURL(file);
      reader.onload = () => {
        try {
          const result = reader.result?.toString() || '';
          // Extrai apenas a parte de dados Base64 se houver um formato data URL
          const base64Content = result.includes(',') ? result.split(',')[1] : result;
          // Envia o resultado para os inscritos
          observer.next(base64Content);
          observer.complete();
        } catch (error) {
          observer.error(new Error('Falha ao processar o arquivo como Base64'));
        }
      };
      reader.onerror = () => {
        observer.error(new Error('Falha ao ler o arquivo'));
      };
    });
  }

  detectMimeFromBytes(file: File): Observable<string> {
    return new Observable<string>(observer => {
      const reader = new FileReader();
      reader.readAsArrayBuffer(file.slice(0, 12));

      reader.onload = () => {
        try {
          const buffer = reader.result as ArrayBuffer;
          const bytes = new Uint8Array(buffer);
          const mime = this.identifyMimeFromBytes(bytes, file.name);
          observer.next(mime);
          observer.complete();
        } catch {
          observer.next(file.type || 'application/octet-stream');
          observer.complete();
        }
      };

      reader.onerror = () => {
        observer.next('application/octet-stream');
        observer.complete();
      };
    });
  }

  private identifyMimeFromBytes(bytes: Uint8Array, fileName: string): string {
    if (bytes.length < 4) {
      return 'application/octet-stream';
    }

    if (bytes[0] === 0x25 && bytes[1] === 0x50 && bytes[2] === 0x44 && bytes[3] === 0x46) {
      return 'application/pdf';
    }

    if (bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) {
      return 'image/jpeg';
    }

    if (bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e && bytes[3] === 0x47) {
      return 'image/png';
    }

    if (
      bytes.length >= 12 &&
      bytes[0] === 0x52 &&
      bytes[1] === 0x49 &&
      bytes[2] === 0x46 &&
      bytes[3] === 0x46 &&
      bytes[8] === 0x57 &&
      bytes[9] === 0x45 &&
      bytes[10] === 0x42 &&
      bytes[11] === 0x50
    ) {
      return 'image/webp';
    }

    if (bytes[0] === 0x50 && bytes[1] === 0x4b && bytes[2] === 0x03 && bytes[3] === 0x04) {
      const ext = fileName.split('.').pop()?.toLowerCase();
      if (ext === 'docx') return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
      if (ext === 'xlsx') return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      return 'application/zip';
    }

    if (bytes[0] === 0xd0 && bytes[1] === 0xcf && bytes[2] === 0x11 && bytes[3] === 0xe0) {
      const ext = fileName.split('.').pop()?.toLowerCase();
      if (ext === 'xls') return 'application/vnd.ms-excel';
      return 'application/msword';
    }

    return 'application/octet-stream';
  }

  buscaArquivo(arquivo: Arquivo, contexto?: ContextoLeituraArquivo): void {
    if (contexto?.historico && (!Number.isInteger(contexto.codAndamento) || (contexto.codAndamento ?? 0) <= 0)) {
      this.popupService.showError('Não foi possível identificar o andamento histórico.');
      return;
    }
    const openTabsFile = this.getOpenTabsFile();
    if (arquivo.SISTEMA !== 1 && !contexto && openTabsFile.value[arquivo.ARQUIVO]) {
      this.fileViewOrDownload(arquivo);
      return;
    }

    const queryParams: QueryParams = {
      sistema: arquivo.SISTEMA === 1 ? 'sgr' : 'intranet',
      arquivo: arquivo.ARQUIVO,
    };
    if (contexto?.codAndamento) queryParams['codandamento'] = contexto.codAndamento;
    if (contexto?.historico) queryParams['historico'] = true;
    this.download({ queryParams, spinnerOff: true })
      .pipe(take(1))
      .subscribe({
        next: (result: SgrResponse) => {
          if (!result['error']) {
            const arquivoBaixado: Arquivo = result['data'][0];
            contexto ? this.fileViewOrDownload(arquivoBaixado, contexto) : this.fileViewOrDownload(arquivoBaixado);
          }
        },
        error: err => {
          console.error(err);
          this.popupService.showError('Erro ao baixar arquivo! Contate o suporte.');
        },
      });
  }

  // Abre o arquivo em uma nova aba (imagem ou pdf) ou inicia o download (outras extensões)
  fileViewOrDownload(file: Arquivo, contexto?: ContextoLeituraArquivo): void {
    try {
      const chaveAba = file.SISTEMA === 1
        ? `sgr:${file.ARQUIVO}:${contexto?.codAndamento ?? 0}:${contexto?.historico ? 'historico' : 'operacional'}`
        : file.ARQUIVO;
      // const fileBlob = this.base64ToBlob(file.BASE64, file.TIPO);
      // const fileURL = URL.createObjectURL(fileBlob);
      // this.downloadFile(fileURL, file.ARQUIVO);
      // setTimeout(() => URL.revokeObjectURL(fileURL), 1000);

      if (file.TIPO.includes('pdf') || file.TIPO.includes('image')) {
        // Recupera as abas abertas com arquivos
        this.openTabsFile = this.getOpenTabsFile();
        // Verifica se já existe uma aba aberta para este arquivo
        if (this.openTabsFile.value[chaveAba]) {
          const currentTab = this.openTabsFile.value[chaveAba];
          // Foca na aba existente
          const existingWindow = window.open('', currentTab['tabId']);
          if (existingWindow && !existingWindow.closed) {
            existingWindow.focus();
            return;
          }
        }
        const fileBlob = this.base64ToBlob(file?.BASE64!, file?.TIPO); // Converte para Blob
        const fileURL = URL.createObjectURL(fileBlob); // Cria a URL do arquivo
        const tabId = this.createOpenBlobTabId(chaveAba); // Distingue arquivo, andamento e modo de leitura.

        // Para PDFs, adiciona parâmetros para melhor visualização
        const targetUrl = file?.TIPO?.includes('pdf') ? fileURL + '#view=FitH' : fileURL;

        // Abre em nova aba
        const newWindow = window.open(targetUrl, tabId);
        if (newWindow) {
          // Armazena a aba no Local Storage
          this.openTabsFile = this.getOpenTabsFile();
          this.openTabsFile.value[chaveAba] = { url: fileURL, tabId: tabId };
          localStorage.setItem(this.openTabsFile.key, JSON.stringify(this.openTabsFile.value));
          // Monitoramento da aba
          const interval = setInterval(() => {
            if (newWindow.closed) {
              clearInterval(interval);
              URL.revokeObjectURL(fileURL); // Libera a URL do objeto
              this.openTabsFile = this.getOpenTabsFile();
              delete this.openTabsFile.value[chaveAba];
              localStorage.setItem(this.openTabsFile.key, JSON.stringify(this.openTabsFile.value));
            }
          }, 1000); // Verifica a cada segundo
        } else {
          // Falha ao abrir a aba, faz download direto
          this.downloadFile(fileURL, file.ARQUIVO);
          setTimeout(() => URL.revokeObjectURL(fileURL), 1000); // Limpa a URL após download
        }
      } else {
        // Para outros tipos de arquivo, faz download direto
        const fileBlob = this.base64ToBlob(file?.BASE64!, file?.TIPO); // Converte para Blob
        const fileURL = URL.createObjectURL(fileBlob); // Cria a URL do arquivo
        this.downloadFile(fileURL, file?.ARQUIVO);
        setTimeout(() => URL.revokeObjectURL(fileURL), 1000); // Limpa a URL após download
      }
    } catch (error) {
      console.error('Erro ao processar o arquivo:', error);
      // Fallback para download direto em caso de erro
      try {
        const fileBlob = this.base64ToBlob(file?.BASE64!, file?.TIPO);
        const fileURL = URL.createObjectURL(fileBlob);
        this.downloadFile(fileURL, file.ARQUIVO);
        setTimeout(() => URL.revokeObjectURL(fileURL), 1000);
      } catch (fallbackError) {
        console.error('Erro no fallback de download:', fallbackError);
      }
    }
  }

  openBlobInNewTab(blob: Blob, key: string): boolean {
    const normalizedKey = key.trim();
    const mimeType = blob?.type?.split(';')[0]?.trim().toLowerCase();
    if (
      !(blob instanceof Blob) ||
      blob.size <= 0 ||
      !normalizedKey ||
      normalizedKey.length > this.maxOpenBlobTabKeyLength ||
      !this.isPreviewableMimeType(mimeType)
    ) {
      throw new Error('O arquivo recebido não pode ser visualizado.');
    }

    const fileURL = URL.createObjectURL(blob);
    const targetUrl = mimeType === 'application/pdf' ? `${fileURL}#view=FitH` : fileURL;
    const currentTab = this.openBlobTabs.get(normalizedKey);

    if (currentTab && this.isWindowOpen(currentTab.window)) {
      try {
        currentTab.window.location.replace(targetUrl);
        const replacedUrl = currentTab.url;
        currentTab.url = fileURL;
        this.revokeObjectUrl(replacedUrl);
        this.focusWindow(currentTab.window);
        return true;
      } catch {
        this.revokeObjectUrl(fileURL);
        throw new Error('Não foi possível atualizar a aba de visualização.');
      }
    }

    if (currentTab) this.releaseOpenBlobTab(normalizedKey, currentTab);

    const tabId = this.createOpenBlobTabId(normalizedKey);
    let newWindow: Window | null;
    try {
      newWindow = window.open(targetUrl, tabId);
    } catch {
      this.revokeObjectUrl(fileURL);
      throw new Error('Não foi possível abrir a aba de visualização.');
    }

    if (!newWindow) {
      this.revokeObjectUrl(fileURL);
      return false;
    }

    const tab: OpenBlobTab = { url: fileURL, tabId, window: newWindow, monitorId: null };
    try {
      tab.monitorId = window.setInterval(() => {
        if (this.isWindowOpen(tab.window)) return;
        this.releaseOpenBlobTab(normalizedKey, tab);
      }, 1000);
      this.openBlobTabs.set(normalizedKey, tab);
      this.focusWindow(newWindow);
      return true;
    } catch {
      this.releaseOpenBlobTab(normalizedKey, tab);
      throw new Error('Não foi possível abrir a aba de visualização.');
    }
  }

  // Método otimizado para preservar integridade de PDFs com assinatura digital
  base64ToBlob(base64: string, mimeType: string): Blob {
    try {
      // Remove prefixo data URL se presente
      const base64Data = base64.includes(',') ? base64.split(',')[1] : base64;
      // Decodifica preservando dados binários
      const binaryString = atob(base64Data);
      const len = binaryString.length;
      const bytes = new Uint8Array(len);
      // Conversão byte a byte para máxima precisão
      for (let i = 0; i < len; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      // Define MIME type correto para PDFs
      const finalMimeType = mimeType.includes('pdf') ? 'application/pdf' : mimeType;
      return new Blob([bytes], { type: finalMimeType });
    } catch (error) {
      console.error('Erro na conversão Base64 para Blob:', error);
      throw error;
    }
  }

  removeFileExtension(fileName: string): string {
    return fileName.replace(/\.[^.]+$/, ''); // Remove a última extensão
  }

  private getOpenTabsFile(): { key: string; value: { [key: string]: any } } {
    let key = 'openTabsFile';
    let value = JSON.parse(this.appLocalStorageService.getItem(key) || '{}');
    return { key, value };
  }

  private releaseOpenBlobTab(key: string, tab: OpenBlobTab): void {
    if (this.openBlobTabs.get(key) === tab) this.openBlobTabs.delete(key);
    if (tab.monitorId !== null) {
      window.clearInterval(tab.monitorId);
      tab.monitorId = null;
    }
    this.revokeObjectUrl(tab.url);
  }

  private isPreviewableMimeType(mimeType: string): boolean {
    return mimeType === 'application/pdf' || mimeType === 'image/jpeg' || mimeType === 'image/png' || mimeType === 'image/webp';
  }

  private createOpenBlobTabId(key: string): string {
    let encodedKey = '';
    for (let index = 0; index < key.length; index++) {
      encodedKey += key.charCodeAt(index).toString(16).padStart(4, '0');
    }
    return `blob_tab_${encodedKey}`;
  }

  private isWindowOpen(tabWindow: Window): boolean {
    try {
      return !tabWindow.closed;
    } catch {
      return false;
    }
  }

  private focusWindow(tabWindow: Window): void {
    try {
      tabWindow.focus();
    } catch {
      // A aba já foi aberta/atualizada; foco é uma melhoria não essencial.
    }
  }

  private revokeObjectUrl(url: string): void {
    try {
      URL.revokeObjectURL(url);
    } catch {
      // Revogação é idempotente e não deve deixar o mapa em estado inconsistente.
    }
  }

  downloadBlob(blob: Blob, fileName: string): void {
    if (!(blob instanceof Blob) || blob.size === 0 || blob.type.toLowerCase() !== 'application/pdf') {
      throw new Error('O servidor não retornou um PDF válido.');
    }
    const url = URL.createObjectURL(blob);
    try { this.downloadFile(url, fileName); }
    catch (error) { URL.revokeObjectURL(url); throw error; }
  }

  // Método auxiliar para iniciar o download de um arquivo
  private downloadFile(url: string, fileName: string): void {
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    try { a.click(); }
    catch (error) { a.remove(); throw error; }

    // Limpeza
    setTimeout(() => {
      a.remove();
      URL.revokeObjectURL(url); // Libera a memória da URL do objeto
    }, 100);
  }

  uploadArquivos(params: {
    sistema: string;
    diretorio: string;
    arquivos: Arquivo[];
    linkParams?: Record<string, number>; // ex: { codespecificacao: 42 } ou { codandamento: 5 }
  }): Observable<Arquivo[]> {
    if (!params.arquivos?.length) return of([]);

    const body = JSON.stringify({
      sistema: params.sistema,
      diretorio: params.diretorio,
      arquivos: params.arquivos,
      ...params.linkParams, // spread preserva o nome da chave exatamente como recebido
    });

    return this.upload({ body }).pipe(
      map((result: SgrResponse) => {
        // erro de negócio
        if (result['error']) {
          throw new SubmitError('UPLOAD', result['msg'] ?? 'Erro ao fazer upload de arquivos');
        }
        return (result['data'] ?? []) as Arquivo[];
      }),
      // erro de transporte
      catchError(err => throwError(() => asSubmitError(err, 'UPLOAD', 'Erro ao fazer upload de arquivos')))
    );
  }

  //!
  //! ========================= CHAMADAS AO BACKEND =========================
  //!

  upload(params: { body: string; spinnerDelay?: number; spinnerOff?: boolean }): Observable<SgrResponse> {
    let endpoint = this.url + '/arquivo';
    let options = this.authService.setHttpOptions(params);
    return this.httpClient.post<SgrResponse>(endpoint, params.body, options);
  }

  download(params?: { queryParams?: QueryParams; spinnerDelay?: number; spinnerOff?: boolean }): Observable<SgrResponse> {
    let endpoint = this.url + '/arquivo';
    let options = this.authService.setHttpOptions(params || {});
    return this.httpClient.get<SgrResponse>(endpoint, options);
  }

  /** @deprecated Use upload() instead */
  saveArquivo(params: { body: string; spinnerDelay?: number; spinnerOff?: boolean }): Observable<SgrResponse> {
    return this.upload(params);
  }

  /** @deprecated Use download() instead */
  getArquivo(params?: { queryParams?: QueryParams; spinnerDelay?: number; spinnerOff?: boolean }): Observable<SgrResponse> {
    return this.download(params);
  }

  updateArquivo(params: { body: string; spinnerDelay?: number; spinnerOff?: boolean }): Observable<SgrResponse> {
    let endpoint = this.url + '/arquivo';
    let options = this.authService.setHttpOptions(params || {});
    return this.httpClient.put<SgrResponse>(endpoint, params?.body, options);
  }

  deleteArquivo(params?: { queryParams?: QueryParams; spinnerDelay?: number; spinnerOff?: boolean }): Observable<SgrResponse> {
    let endpoint = this.url + '/arquivo';
    let options = this.authService.setHttpOptions(params || {});
    return this.httpClient.delete<SgrResponse>(endpoint, options);
  }
}

interface OpenBlobTab {
  url: string;
  tabId: string;
  window: Window;
  monitorId: number | null;
}
